// src/pages/products/ManageProductTagsPage.jsx
import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MoreVertical,
  AlertCircle,
  RefreshCw,
  Save,
  X,
  Calendar,
  Clock
} from 'lucide-react';
import { 
  getProductTags, 
  createProductTag, 
  getProductTag, 
  updateProductTag, 
  deleteProductTag 
} from '../../services/api/productTags';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './ManageProductTagsPage.module.css';

const ManageProductTagsPage = () => {
  const { hasPermission } = usePermissions();
  const { t, formatDate } = useLocalization();
  const { success, error, confirm } = useAlert();
  
  // State management
  const [tags, setTags] = useState([]);
  const [filteredTags, setFilteredTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Modal/Form states
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    name: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load tags on component mount
  useEffect(() => {
    fetchTags();
  }, []);

  // Filter tags when search query or tags change
  useEffect(() => {
    filterTags();
  }, [tags, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTags = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);
      const response = await getProductTags();
      setTags(response || []);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      setLoadingError(t('productTags.failedToLoadTags'));
    } finally {
      setIsLoading(false);
    }
  };

  const filterTags = () => {
    if (!searchQuery.trim()) {
      setFilteredTags(tags);
      return;
    }

    const filtered = tags.filter(tag =>
      tag.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredTags(filtered);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('productTags.nameRequired');
    } else if (formData.name.trim().length < 2) {
      errors.name = t('productTags.nameMinLength');
    } else if (formData.name.trim().length > 50) {
      errors.name = t('productTags.nameMaxLength');
    }

    // Check for duplicate names (excluding current tag when editing)
    const existingTag = tags.find(tag => 
      tag.name.toLowerCase() === formData.name.trim().toLowerCase() &&
      (!editingTag || tag.id !== editingTag.id)
    );
    
    if (existingTag) {
      errors.name = t('productTags.duplicateNameError');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '' });
    setFormErrors({});
    setEditingTag(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = async (tagId) => {
    try {
      setIsSubmitting(true);
      const response = await getProductTag(tagId);
      const tag = response;
      
      setFormData({
        name: tag.name || ''
      });
      setEditingTag(tag);
      setShowForm(true);
      setActiveDropdown(null);
    } catch (err) {
      console.error('Failed to fetch tag details:', err);
      error(t('productTags.failedToLoadTag'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (tagId, tagName) => {
    const confirmed = await confirm(
      t('productTags.confirmDelete', { tagName }),
      {
        confirmText: t('common.delete'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await deleteProductTag(tagId);
      setTags(prev => prev.filter(t => t.id !== tagId));
      success(t('productTags.tagDeletedSuccess', { tagName }));
    } catch (err) {
      console.error('Failed to delete tag:', err);
      error(t('productTags.failedToDelete'));
    }
    setActiveDropdown(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormErrors({});

    try {
      const submitData = {
        name: formData.name.trim()
      };

      if (editingTag) {
        // Update existing tag
        const response = await updateProductTag(editingTag.id, submitData);
        const updatedTag = response.tag;
        setTags(prev => 
          prev.map(t => t.id === editingTag.id ? updatedTag : t)
        );
        success(t('productTags.tagUpdatedSuccess', { tagName: updatedTag.name }));
      } else {
        // Create new tag
        const response = await createProductTag(submitData);
        const newTag = response.tag;
        setTags(prev => [...prev, newTag]);
        success(t('productTags.tagCreatedSuccess', { tagName: newTag.name }));
      }

      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save tag:', err);
      if (err.response?.data?.message) {
        setFormErrors({ submit: err.response.data.message });
      } else {
        setFormErrors({ 
          submit: editingTag 
            ? t('productTags.failedToUpdate') 
            : t('productTags.failedToCreate') 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    resetForm();
  };

  const toggleDropdown = (tagId) => {
    setActiveDropdown(activeDropdown === tagId ? null : tagId);
  };

  if (isLoading) {
    return (
      <div className={styles.manageTagsPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Tag className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('productTags.title')}</h1>
                <p>{t('productTags.description')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('productTags.loadingTags')}</h3>
          <p>{t('productTags.loadingTagsDesc')}</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.manageTagsPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Tag className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('productTags.title')}</h1>
                <p>{t('productTags.description')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('errors.somethingWentWrong')}</h3>
          <p>{loadingError}</p>
          <button onClick={fetchTags} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.manageTagsPage}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Tag className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('productTags.title')}</h1>
              <p>{t('productTags.description')}</p>
            </div>
          </div>
          
          {hasPermission('create_product_tag') && (
            <div className={styles.headerActions}>
              <button onClick={handleCreateNew} className={styles.primaryButton}>
                <Plus size={20} />
                {t('productTags.addTag')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className={styles.pageControls}>
        <div className={styles.searchSection}>
          <div className={styles.searchInputWrapper}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              placeholder={t('productTags.searchTags')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
        
        <div className={styles.controlsRight}>
          <div className={styles.resultsInfo}>
            {searchQuery ? (
              <span>{t('productTags.filteredResults', { 
                count: filteredTags.length,
                total: tags.length 
              })}</span>
            ) : (
              <span>{t('productTags.totalTags', { count: tags.length })}</span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.pageContent}>
        {filteredTags.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery ? (
              <>
                <Search className={styles.emptyIcon} size={48} />
                <h3>{t('productTags.noSearchResults')}</h3>
                <p>{t('productTags.noSearchResultsDesc')}</p>
                <button 
                  onClick={() => setSearchQuery('')} 
                  className={styles.secondaryButton}
                >
                  {t('common.clearSearch')}
                </button>
              </>
            ) : (
              <>
                <Tag className={styles.emptyIcon} size={48} />
                <h3>{t('productTags.noTags')}</h3>
                <p>{t('productTags.noTagsDesc')}</p>
                {hasPermission('create_product_tag') && (
                  <button onClick={handleCreateNew} className={styles.primaryButton}>
                    <Plus size={20} />
                    {t('productTags.createFirstTag')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className={styles.tagsTableContainer}>
            <table className={styles.tagsTable}>
              <thead>
                <tr>
                  <th className={styles.nameColumn}>{t('productTags.name')}</th>
                  <th className={styles.createdColumn}>{t('productTags.createdAt')}</th>
                  <th className={styles.updatedColumn}>{t('productTags.updatedAt')}</th>
                  <th className={styles.actionsColumn}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredTags.map((tag) => (
                  <tr key={tag.id} className={styles.tagRow}>
                    <td className={styles.nameCell}>
                      <div className={styles.tagInfo}>
                        <Tag className={styles.tagIcon} size={16} />
                        <span className={styles.tagName}>{tag.name}</span>
                      </div>
                    </td>
                    
                    <td className={styles.dateCell}>
                      <div className={styles.dateInfo}>
                        <Calendar size={12} />
                        <span>{formatDate(tag.createdAt)}</span>
                      </div>
                    </td>
                    
                    <td className={styles.dateCell}>
                      <div className={styles.dateInfo}>
                        <Clock size={12} />
                        <span>{formatDate(tag.updatedAt)}</span>
                      </div>
                    </td>
                    
                    <td className={styles.actionsCell}>
                      <div className={styles.actionsWrapper}>
                        {hasPermission('edit_product_tag') && (
                          <button 
                            onClick={() => handleEdit(tag.id)}
                            className={`${styles.actionButton} ${styles.editButton}`}
                            title={t('common.edit')}
                          >
                            <Edit size={14} />
                          </button>
                        )}
                        
                        {hasPermission('delete_product_tag') && (
                          <div className={styles.dropdownWrapper}>
                            <button
                              className={`${styles.actionButton} ${styles.moreButton}`}
                              onClick={() => toggleDropdown(tag.id)}
                              title={t('common.moreActions')}
                            >
                              <MoreVertical size={14} />
                            </button>
                            
                            {activeDropdown === tag.id && (
                              <div className={styles.dropdownMenu}>
                                <button
                                  className={`${styles.dropdownItem} ${styles.deleteItem}`}
                                  onClick={() => handleDelete(tag.id, tag.name)}
                                >
                                  <Trash2 size={14} />
                                  {t('common.delete')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>
                {editingTag ? t('productTags.editTag') : t('productTags.createNewTag')}
              </h2>
              <button onClick={handleCancelForm} className={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>

            {formErrors.submit && (
              <div className={styles.errorBanner}>
                <AlertCircle size={20} />
                <p>{formErrors.submit}</p>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label htmlFor="name">{t('productTags.tagName')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('productTags.tagNamePlaceholder')}
                  className={formErrors.name ? 'error' : ''}
                  required
                  autoFocus
                  maxLength={50}
                />
                {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
                <span className={styles.hintText}>{t('productTags.nameHint')}</span>
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={handleCancelForm}
                  className={styles.cancelButton}
                >
                  <X size={18} />
                  {t('common.cancel')}
                </button>
                <button 
                  type="submit" 
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className={styles.loadingSpinner}></div>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingTag ? t('productTags.updateTag') : t('productTags.createTag')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProductTagsPage;